
import { Component, Vue, Watch } from "vue-property-decorator";
import { namespace, Getter } from "vuex-class";
import { AxiosResponse } from 'axios';

import ConfirmationModal from "@/components-shared/ConfirmationModal.vue"
import { ReportState, FavoriteReportModal, ReportFavoriteData } from "@/store/reports/types";
import FavoriteReportsApi, { reportApiResponse } from "@/services/api.insightReports.service.ts";
import { ConfirmationMessage, enumMessageType } from '@/store/types';
import insightReportsApi from '@/services/api.insightReports.service.ts';

const Reports = namespace("reports");

@Component({
    components: {
        "confirmation-modal": ConfirmationModal,
    }
})

export default class FavouriteReports extends Vue {
    @Reports.Mutation setFavoriteReportModalShown: (favoriteReportModal: FavoriteReportModal) => void;
    @Reports.State((state: ReportState) => state.FavoriteReportModal) private favoriteReportModal: FavoriteReportModal;
    @Reports.State((state: ReportState) => state.FavoriteReports) private favoriteReports: ReportFavoriteData[];
    @Reports.Getter("getReportTypes") reportTypes;
    @Getter("getUserId") public userId: number;
    @Reports.Action fetchReportTypes: any;

    private favoriteData: ReportFavoriteData = {
        insightsFavoriteID: 0,
        name: "",
        userID: 0,
        insightID: 0,
        queryString: ""
    };

    private showConfirmationModal: boolean = false;
    private confirmModalOkButtonText: string = null;
    private confirmModalCancelButtonText: string = null;
    private confirmModalDeletion: boolean = false;
    private confirmModalTitle: string = "Confirmation Modal";
    private confirmationMessages: ConfirmationMessage[] = [];
    private confirmationModalMessage: string = "";

    @Watch('favoriteReportModal.shown')
    onModalShown(): void{
        this.favoriteData.insightsFavoriteID = this.favoriteReportModal.favoriteID;
        
        if (this.favoriteReportModal.favoriteID == 0) {
            //Get Name of report
            let favoriteName : string = "";
            this.reportTypes.forEach(rt => {
                let foundReport = this.findReportName(rt);
                if (foundReport != null) {
                    favoriteName = (foundReport.customTitle ? foundReport.customTitle : foundReport.title);
                    this.favoriteData.insightID = foundReport.insightID;
                } 
            });

            let dateTime = new Date();
            this.favoriteData.name = favoriteName + " " + dateTime.toLocaleDateString() + "-" + dateTime.getHours() + ":" + dateTime.getMinutes().toString().padStart(2, "0");

            //Get Query String 
            this.favoriteData.queryString = this.favoriteReportModal.reportPermalink.split('?')[1];

            this.favoriteData.userID = this.userId;
        }
        else {
            this.favoriteData = this.favoriteReports.find(f => f.insightsFavoriteID == this.favoriteReportModal.favoriteID);
        }
    }

    private findReportName(reportType) {
        let report =  reportType.types.find(t => t.endpoint == this.favoriteReportModal.currentUrl);
        if (report != null) {
            return report;
        } 
        else { 
            return null;
        }
    } 

    private get editMode() : boolean{
        if (this.favoriteReportModal.favoriteID > 0) {
            return true;
        }
        return false;
    }

    public closeFavoriteReportModal() : void {
        this.favoriteData = {
            insightsFavoriteID: 0,
            name: "",
            userID: 0,
            insightID: 0,
            queryString: ""
        };
        
		this.setFavoriteReportModalShown({shown: false, favoriteID: 0, currentUrl: "", reportPermalink: ""});
	}

    private get validation() : boolean {
        if (this.invalidFeedback == "") {
            return true;
        }
        else {
            return false;
        }
    }

    private get invalidFeedback() : string {
        let found = this.favoriteReports.find(r => r.name.toLowerCase() == this.favoriteData.name.toLowerCase());
        if (found != null && !this.editMode) {
            return "Favorite name already exists";
        }
        else if (this.favoriteData.name == "") {
            return "Favorite name must be given";
        }

        return "";
    }

    private async setFavorite() : Promise<void> {
        let response : reportApiResponse = await FavoriteReportsApi.createFavoriteReport(this.favoriteData);
        if (response.success == true) {
            this.closeConfirmationModal();
            this.closeFavoriteReportModal();
            await this.fetchReportTypes();

            let result : ReportFavoriteData = response.result as ReportFavoriteData;
            this.$emit("favorite-added", result.insightsFavoriteID);
        }
        else {
            this.openConfirmationModal("Favorite Report Error", this.sortMessages(response), false, "", "OK", "")
        }
    }

    private sortMessages(response: reportApiResponse) : ConfirmationMessage[]{
        let messages : ConfirmationMessage[] = [];
        response.errors.forEach(error => {
            messages.push({messageType: enumMessageType.error, text: error});
        });
        response.warnings.forEach(warning => {
            messages.push({messageType: enumMessageType.warning, text: warning})
        });
        response.warnings.forEach(confirm => {
            messages.push({messageType: enumMessageType.confirmation, text: confirm})
        });

        return messages;
    }

    private openConfirmationModal(title: string, messages: ConfirmationMessage[], deletion: boolean, okText: string, cancelText: string, standardMessage: string) : void {
        this.confirmModalDeletion = deletion;
        this.confirmModalOkButtonText = okText;
        this.confirmModalCancelButtonText = cancelText;
        this.confirmModalTitle = title;
        this.showConfirmationModal = true;
        this.confirmationMessages = messages;
        this.confirmationModalMessage = standardMessage;
    }

    private closeConfirmationModal() : void { 
        this.showConfirmationModal = false;
        this.confirmModalDeletion = false;
        this.confirmModalOkButtonText = "";
        this.confirmModalCancelButtonText = "OK";
        this.confirmationMessages = [];
        this.confirmModalTitle = "";
    }

    private confirmRemoveFavorite() : void {
        let message: string = "Are you sure you want to remove '" + this.favoriteData.name + "' ?"; 
        this.openConfirmationModal("Delete Favorited Report", [], true, "DELETE", "Cancel", message);
    }

    private async removeFavorite() : Promise<void> {
        let response: reportApiResponse; 
        response = await insightReportsApi.removeFavoriteReport(this.favoriteData.insightsFavoriteID);

        if (response.success == true){
                this.$emit("favorite-removed", this.favoriteData.insightsFavoriteID);

                //If response is successful close modal 
                this.closeConfirmationModal();
                this.closeFavoriteReportModal();
                await this.fetchReportTypes();
        } 
        else {
            this.openConfirmationModal("Delete Custom Report Error", this.sortMessages(response), false, "", "OK", "")
        }

    }
}
